import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { fromJS } from 'immutable';
import { useSelector } from 'react-redux';
import MatLayout from '../Board/MatLayout';
import { pictureSpot } from '../Board/pictureSpot';
import { selectCardsById } from '../Cards/selectors';
import { cardImageUrl } from '../services/helpers';
import { Muted } from '../Controls';
import { placeSelector, replayFlights } from './flights';
import { HandFan } from '../Board/OpponentHand';
import CardZoom from '../CardZoom';
import PileView from '../CardZoom/PileView';

const FLIGHT_MS = 450;
const STAGGER_MS = 90;

/**
 * Flies a card's picture from one box on screen to another, then takes it away. One that `turnsOver` (your draw or
 * prize) leaves face down and turns face up halfway, narrowing to its edge and widening again, as in the game.
 */
function fly(card, from, to, delay, turnsOver = false) {
  const img = document.createElement('img');
  img.src = cardImageUrl(turnsOver ? null : card);
  img.alt = '';
  Object.assign(img.style, {
    position: 'fixed', left: `${from.left}px`, top: `${from.top}px`, width: `${from.width}px`, zIndex: 40,
    pointerEvents: 'none', borderRadius: `${from.width * 0.051}px`, filter: 'drop-shadow(0 6px 10px rgba(0,0,0,.45))',
  });
  document.body.appendChild(img);
  const scale = to.width / from.width;
  const dx = to.left + to.width / 2 - (from.left + from.width / 2);
  const dy = to.top + to.height / 2 - (from.top + from.height / 2);
  const at = (fraction, width) => `translate(${dx * fraction}px, ${dy * fraction}px) scale(${(1 + (scale - 1) * fraction) * width}, ${1 + (scale - 1) * fraction})`;
  img.animate(
    turnsOver
      ? [{ transform: at(0, 1) }, { transform: at(0.5, 0), offset: 0.5 }, { transform: at(1, 1) }]
      : [{ transform: at(0, 1) }, { transform: at(1, 1) }],
    { duration: FLIGHT_MS, delay, easing: turnsOver ? 'linear' : 'cubic-bezier(.2, .7, .2, 1)', fill: 'both' },
  ).onfinish = () => img.remove();
  if (turnsOver) {
    setTimeout(() => { img.src = cardImageUrl(card); }, delay + FLIGHT_MS / 2);
  }
}

/**
 * The mat as it was after the replay's move, in the board's own place (so it keeps the game's size). Stepping
 * forward, each card that moved flies from where it was to where it went (replayFlights).
 */
const ReplayBoard = ({ replay, userId, turned }) => {
  const cardsById = useSelector(selectCardsById);
  const boxes = useRef(new Map());
  // What's being looked at: a card full size, or a discard pile ({ card } or { pile, title }).
  const [looking, setLooking] = useState(null);
  const { entry, forward } = replay;

  // Where every place was as the previous move's board was drawn, by its selector: a card flies from there (a card
  // that left your hand is gone from it now) to where it is on this move's board.
  useLayoutEffect(() => {
    const before = boxes.current;
    const box = (selector) => {
      const element = selector && document.querySelector(selector);
      return element ? element.getBoundingClientRect() : null;
    };
    const flights = entry ? replayFlights(entry) : [];
    if (entry && forward) {
      flights.forEach(([owner, from, to, cardId], i) => {
        const mine = owner === userId;
        const fromSelector = placeSelector(from, mine, owner, cardId);
        const fromBox = before.get(fromSelector) || box(fromSelector);
        const toBox = box(placeSelector(to, mine, owner, cardId));
        if (fromBox && toBox && fromBox.width > 0 && toBox.width > 0) {
          const turnsOver = mine && !!cardId && (from === 'deck' || from === 'prize') && to === 'hand';
          fly(cardId ? cardsById.get(cardId) : null, fromBox, toBox, i * STAGGER_MS, turnsOver);
        }
      });
    }
    // Remembered for the next step: every place on the mat, and each card in your hand.
    const now = new Map();
    document.querySelectorAll('[data-replay-board] [data-zone]').forEach((element) => {
      const side = element.closest('[data-side]');
      const zone = element.dataset.zone;
      const place = zone === 'bench' ? `bench:${element.dataset.position}` : zone;
      const mine = side && side.dataset.side === 'player';
      now.set(placeSelector(place, mine, null), element.getBoundingClientRect());
    });
    document.querySelectorAll('[data-replay-hand] [data-card-id]').forEach((element) => {
      now.set(`[data-replay-hand] [data-card-id="${element.dataset.cardId}"]`, element.getBoundingClientRect());
    });
    boxes.current = now;
  }, [entry, forward, userId, cardsById]);

  if (!replay.entries) {
    return <Muted style={{ padding: 16 }}>Loading the replay…</Muted>;
  }
  if (!entry) {
    return <Muted style={{ padding: 16 }}>Nothing to replay yet: moves are kept for replaying from now on.</Muted>;
  }

  const board = fromJS(entry.board);
  const side = (mine) => board.find(s => (s.get('user_id') === userId) === mine);
  // A face-up card to see full size, or a discard pile to look through, as it was at this move.
  const pick = (zone, card, picked) => {
    if (card) {
      setLooking({ card });
    } else if (zone === 'discard') {
      const pile = (picked.get('discard_cards') || []).map(id => cardsById.get(id)).filter(Boolean).toArray();
      setLooking({ pile, title: picked.get('user_id') === userId ? 'Your discard pile' : "Your opponent's discard pile" });
    }
  };
  return (
    <div data-replay-board style={{ display: 'contents' }}>
      <MatLayout
        turned={turned}
        opponentSpot={pictureSpot(side(false), cardsById, pick)}
        playerSpot={pictureSpot(side(true), cardsById, pick)}
        // The opponent's hand as it was then, face down: how many cards they held.
        opponentHand={<div data-replay-opponent-hand style={{ width: '100%', height: '100%' }}><HandFan count={side(false) ? side(false).get('hand') || 0 : 0} /></div>}
      />
      {looking && looking.card && <CardZoom card={looking.card} onClose={() => setLooking(null)} />}
      {looking && looking.pile && <PileView title={looking.title} cards={looking.pile} onClose={() => setLooking(null)} />}
    </div>
  );
};

ReplayBoard.propTypes = {
  replay: PropTypes.object.isRequired,
  userId: PropTypes.number.isRequired,
  turned: PropTypes.bool,
};

export default ReplayBoard;
